export const NIGERIAN_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "Federal Capital Territory",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
] as const;

export type NigerianState = (typeof NIGERIAN_STATES)[number];
export type SeoDirectoryKind = "properties" | "realtors" | "developers";

export const POPULAR_NIGERIAN_STATES = [
  "Lagos",
  "Federal Capital Territory",
  "Rivers",
  "Oyo",
  "Ogun",
  "Delta",
  "Kaduna",
  "Kano",
  "Enugu",
  "Edo",
] as const satisfies readonly NigerianState[];

export interface StateProfileCandidate {
  id?: string;
  slug?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  business_name?: string;
  listed_properties_count?: number;
  listed_propeties_count?: number;
  is_partner?: boolean;
  verified?: boolean;
  kyc_status?: string;
  status?: string;
  is_public?: boolean;
}

export function toStateSlug(state: string): string {
  return state
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function stateFromSlug(slug: string): NigerianState | null {
  if (slug.toLowerCase() === "abuja" || slug.toLowerCase() === "fct") {
    return "Federal Capital Territory";
  }
  return (
    NIGERIAN_STATES.find((state) => toStateSlug(state) === slug.toLowerCase()) ??
    null
  );
}

function normalizedState(value?: string | null): string {
  return (value || "")
    .toLowerCase()
    .replace(/\bstate\b/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function stateFromName(value?: string | null): NigerianState | null {
  const actual = normalizedState(value);
  return (
    NIGERIAN_STATES.find((state) => normalizedState(state) === actual) ??
    (["fct", "abuja"].includes(actual)
      ? "Federal Capital Territory"
      : null)
  );
}

export function canonicalStateSlug(value?: string | null): string | null {
  const state = stateFromName(value);
  return state ? toStateSlug(state) : null;
}

export function isIndexableStateProfile(
  entry: StateProfileCandidate,
): boolean {
  if (
    !entry.id &&
    !entry.slug
  ) {
    return false;
  }
  if (
    !entry.business_name &&
    !entry.full_name &&
    !entry.first_name &&
    !entry.last_name
  ) {
    return false;
  }
  if (entry.is_public === false) return false;
  const status = entry.status?.toLowerCase();
  if (
    status &&
    ["draft", "inactive", "pending", "rejected", "deleted"].includes(status)
  ) {
    return false;
  }

  const propertyCount =
    entry.listed_properties_count ?? entry.listed_propeties_count ?? 0;
  return (
    propertyCount > 0 ||
    entry.verified === true ||
    entry.is_partner === true ||
    entry.kyc_status?.toLowerCase() === "verified"
  );
}
